import type {SetStateAction} from 'react';
import type {ChatMessage, ChatMessageModelImagePart, ChatMessagePart, ControlStreamEvent, ModelMessage} from '@Components/ai-chat-window/ai-chat-window.types';
import type {HistoryItem} from '@AiChat/ai-chat.types';
import {ChatMessagePartType, ControlEventType, MessageRole} from '@Components/ai-chat-window/ai-chat-window.types';
import {appendSkeletonToMessage, getLastThinkingMessage, isComponentPart, replaceThinkingOrAppendMessage, replaceThinkingOrUpdateMessage} from '@Components/ai-chat-window/lib/ai-chat-stream.library';
import {createComponentPart} from '@Components/ai-chat-window/lib/stream-component-registry';
import {getSkeletonDimensions} from '@Components/ai-chat-window/lib/chat-attachment-image.library';
import {MAX_CHAT_IMAGE_DIMENSION} from '@Components/ai-chat-window/components/generated-image/generated-image.types';
import {showPurchaseCreditsDialog} from '@Libraries/ai-chat.library';
import {shareAiChatImage} from '@Components/ai-chat-window/lib/share-design.library';
import {getUniqueString} from '@Utils/string.util';

export interface ControlEventHandlerDeps {
  debouncedRefreshChatHistory: () => void;
  onNewChat?: () => void;
  onUpdateHistoryItem?: (chatId: string, updates: Partial<HistoryItem>) => void;
  onSetSearchTags?: (chatId: string, tags: string[]) => void;
  getCurrentMessages: (chatId: string | undefined) => Record<string, ChatMessage>;
  setMessages: (chatId: string, updater: SetStateAction<Record<string, ChatMessage>>) => void;
  refreshCredits?: () => void;
  applyMessageEdit: (chatId: string, editedMessageId: string, historyFromServer: ChatMessage[]) => void;
  openPosterInEditor: (hashedId: string) => void;
}

type ControlEventHandler = (event: ControlStreamEvent, deps: ControlEventHandlerDeps) => void;

const isImageSkeleton = (part: ChatMessagePart): boolean =>
  part.type === ChatMessagePartType.MODEL_GENERATED_IMAGE && 'isSkeletonPlaceholder' in part && Boolean(part.isSkeletonPlaceholder);

const skeletonMessage = (part: ChatMessagePart, isThinking: boolean): ModelMessage => ({
  role: MessageRole.ASSISTANT,
  isThinking,
  parts: [part],
  messageId: getUniqueString(),
  feedback: null,
  usage: {inputTokens: 0, outputTokens: 0},
});

const newChat: ControlEventHandler = (_event, deps) => {
  deps.debouncedRefreshChatHistory();
  deps.onNewChat?.();
};

const thumbnailUrl: ControlEventHandler = (event, deps) => {
  if (!event.idAiChat || !event.data?.assetHashedFilename) {
    return;
  }

  deps.onUpdateHistoryItem?.(event.idAiChat, {assetHashedFilename: event.data.assetHashedFilename});
};

const chatTitle: ControlEventHandler = (event, deps) => {
  if (!event.idAiChat || !event.data?.chatTitle) {
    return;
  }

  deps.onUpdateHistoryItem?.(event.idAiChat, {title: event.data.chatTitle});
};

const searchTags: ControlEventHandler = (event, deps) => {
  if (!event.idAiChat || !event.data?.searchTags) {
    return;
  }

  deps.onSetSearchTags?.(event.idAiChat, event.data.searchTags);
};

const imageSkeleton: ControlEventHandler = (event, deps) => {
  const chatId = event.idAiChat;
  const currentMessages = deps.getCurrentMessages(chatId);
  const dimensions = getSkeletonDimensions(event.data?.width, event.data?.height, event.data?.aspectRatio, MAX_CHAT_IMAGE_DIMENSION);

  const part: ChatMessageModelImagePart = {
    type: ChatMessagePartType.MODEL_GENERATED_IMAGE,
    hashedId: '',
    width: dimensions.width,
    height: dimensions.height,
    isSkeletonPlaceholder: true,
    isNewGeneration: true,
  };

  const thinkingMessage = getLastThinkingMessage(currentMessages);

  if (thinkingMessage?.messageId) {
    const updated = appendSkeletonToMessage(thinkingMessage, part, isImageSkeleton);
    deps.setMessages(chatId, {...currentMessages, [thinkingMessage.messageId]: updated});
    return;
  }

  const fresh = replaceThinkingOrUpdateMessage(currentMessages, skeletonMessage(part, false), (existing) => appendSkeletonToMessage(existing, part, isImageSkeleton));
  deps.setMessages(chatId, fresh);
};

const entitySkeleton: ControlEventHandler = (event, deps) => {
  const componentType = event.data?.componentType;

  if (!componentType) {
    return;
  }

  const part = createComponentPart({componentType, props: {mode: event.data?.mode}});

  if (!part) {
    return;
  }

  const chatId = event.idAiChat;
  const currentMessages = deps.getCurrentMessages(chatId);
  const matchesComponentType = (candidate: ChatMessagePart): boolean => isComponentPart(candidate, part.componentType);
  const thinkingMessage = getLastThinkingMessage(currentMessages);

  if (thinkingMessage?.messageId) {
    const updated = appendSkeletonToMessage(thinkingMessage, part, matchesComponentType);
    deps.setMessages(chatId, {...currentMessages, [thinkingMessage.messageId]: updated});
    return;
  }

  deps.setMessages(chatId, replaceThinkingOrAppendMessage(currentMessages, skeletonMessage(part, true)));
};

const creditsRefresh: ControlEventHandler = (_event, deps) => {
  deps.refreshCredits?.();
};

const messageEdit: ControlEventHandler = (event, deps) => {
  const editedMessageId = event.data?.editedMessageId;
  if (!event.idAiChat || !editedMessageId) {
    return;
  }

  deps.applyMessageEdit(event.idAiChat, editedMessageId, event.data?.history ?? []);
};

const insufficientCredits: ControlEventHandler = () => {
  void showPurchaseCreditsDialog();
};

const share: ControlEventHandler = (event) => {
  const imageHashedId = event.data?.imageHashedId;
  if (!imageHashedId) {
    return;
  }

  void shareAiChatImage(imageHashedId);
};

const openInEditor: ControlEventHandler = (event, deps) => {
  const imageHashedId = event.data?.imageHashedId;
  if (!imageHashedId) {
    return;
  }

  deps.openPosterInEditor(imageHashedId);
};

const CONTROL_EVENT_HANDLERS: Partial<Record<ControlEventType, ControlEventHandler>> = {
  [ControlEventType.NEW_CHAT]: newChat,
  [ControlEventType.THUMBNAIL_URL]: thumbnailUrl,
  [ControlEventType.CHAT_TITLE]: chatTitle,
  [ControlEventType.SEARCH_TAGS]: searchTags,
  [ControlEventType.IMAGE_STREAMING_START]: imageSkeleton,
  [ControlEventType.ENTITY_SKELETON_START]: entitySkeleton,
  [ControlEventType.CREDITS_REFRESH]: creditsRefresh,
  [ControlEventType.MESSAGE_EDIT_APPLIED]: messageEdit,
  [ControlEventType.INSUFFICIENT_CREDITS]: insufficientCredits,
  [ControlEventType.SHARE]: share,
  [ControlEventType.OPEN_IN_EDITOR]: openInEditor,
};

export function dispatchControlEvent(event: ControlStreamEvent, deps: ControlEventHandlerDeps): void {
  CONTROL_EVENT_HANDLERS[event.type]?.(event, deps);
}
